import './PlansTable.scss'

import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { getModalTriggerId } from 'Root/Shared/components/PuxModal/PuxModal'
import { PuxWysiwygHtmlBodyType } from 'Shared/components/PuxWysiwyg/PuxWysiwyg'
import { t } from 'ttag'

import PlansTableEnterprisePanel from './PlansTableEnterprisePanel'
import PlansTableFeatureCell from './PlansTableFeatureCell'
import PlansTableHostingSwitch from './PlansTableHostingSwitch'
import PlansTableInfoIcon from './PlansTableInfoIcon'
import PlansTablePlanHeader from './PlansTablePlanHeader'
import PlansTablePrice from './PlansTablePrice'
import {
  formatCurrencyPrice,
  getDefaultCurrencyIndex,
  getPricingIsoCode,
} from './pricingFormatters'
import { ENTERPRISE_PLAN, PRICING_PLANS, PricingPlan } from './pricingPlans'
import {
  ContentPickerType,
  MaybeList,
  PricingCurrencyType,
  PricingFeatureType,
  PricingHosting,
} from './pricingTypes'

interface PlansTableProps {
  buyLink: ContentPickerType
  currencies: MaybeList<PricingCurrencyType>
  editorPath: string
  enterpriseContent: PuxWysiwygHtmlBodyType
  features: MaybeList<PricingFeatureType>
  pageContentItemId: string
  wysiwygId: string
}

const project: string = process.env.PROJECT
  ? process.env.PROJECT
  : `EasyRedmineGatsby`

const PlansTable: FunctionComponent<PlansTableProps> = (props) => {
  const currencies = Array.isArray(props.currencies) ? props.currencies : []
  const features = Array.isArray(props.features) ? props.features : []
  const isoCode = getPricingIsoCode(project, process.env.LOCALE)
  const [currencyIdx, setCurrencyIdx] = useState(0)
  const [selectedHosting, setSelectedHosting] = useState<PricingHosting>(
    `cloud`
  )
  const [hosting, setHosting] = useState<PricingHosting>(`cloud`)
  const [fadingOut, setFadingOut] = useState(false)
  const hostingTimeout = useRef<number>()
  const activeCurrency = currencies[currencyIdx]

  useEffect(() => () => window.clearTimeout(hostingTimeout.current), [])

  const changeHosting = (nextHosting: PricingHosting) => {
    if (nextHosting === selectedHosting) return

    window.clearTimeout(hostingTimeout.current)
    setSelectedHosting(nextHosting)

    if (
      nextHosting === hosting ||
      window.matchMedia(`(prefers-reduced-motion: reduce)`).matches
    ) {
      setHosting(nextHosting)
      setFadingOut(false)
      return
    }

    setFadingOut(true)
    hostingTimeout.current = window.setTimeout(() => {
      setHosting(nextHosting)
      setFadingOut(false)
    }, 240)
  }

  useEffect(() => {
    const cookie = document.cookie.match(
      `(^|;)\\s*` + `awscountry` + `\\s*=\\s*([^;]+)`
    )

    setCurrencyIdx(getDefaultCurrencyIndex(currencies, cookie?.[2]))
  }, [currencies])

  const enterpriseGridStyle = useMemo(
    () => ({
      gridRow: `2 / span ${features.length}`,
    }),
    [features.length]
  )

  const openModal = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    document.getElementById(getModalTriggerId(`talk`))?.click()
  }

  const renderPlanHeader = (plan: PricingPlan) => (
    <PlansTablePlanHeader
      hosting={hosting}
      key={plan.id}
      onEnterpriseClick={openModal}
      plan={plan}
    >
      {activeCurrency && !plan.enterprise ? (
        <>
          <PlansTablePrice
            price={formatCurrencyPrice(activeCurrency, plan.id, isoCode)}
          />
          <p className='PlansTable-amount'>
            {hosting === `on-premises`
              ? t`Redmine.Pricing.BilledAnnually`
              : t`Redmine.Pricing.UserPerMonth`}
          </p>
        </>
      ) : null}
    </PlansTablePlanHeader>
  )

  const renderFeatureLabel = (feature: PricingFeatureType) => (
    <div
      className='PlansTable-feature-label'
      key={`label-${feature.contentItemId}`}
    >
      <span>{feature.displayText}</span>
      <PlansTableInfoIcon
        title={feature.pricingFeatureTooltip || feature.displayText}
      />
    </div>
  )

  const renderPlanCells = (feature: PricingFeatureType, index: number) =>
    PRICING_PLANS.map((plan) => (
      <PlansTableFeatureCell
        feature={feature}
        first={index === 0}
        key={`${feature.contentItemId}-${plan.id}`}
        last={index === features.length - 1}
        plan={plan}
      />
    ))

  return (
    <div
      className={`PlansTable${
        hosting === `on-premises` ? ` PlansTable--on-premises` : ``
      }${fadingOut ? ` PlansTable--fading-out` : ``}`}
    >
      <div className='PlansTable-header'>
        <PlansTableHostingSwitch
          modifier='mobile'
          onChange={changeHosting}
          value={selectedHosting}
        />
      </div>

      <div className='PlansTable-grid'>
        <div className='PlansTable-grid-header PlansTable-grid-header--label'>
          <PlansTableHostingSwitch
            modifier='desktop'
            onChange={changeHosting}
            value={selectedHosting}
          />
        </div>
        {PRICING_PLANS.map(renderPlanHeader)}
        {renderPlanHeader(ENTERPRISE_PLAN)}
        <PlansTableEnterprisePanel
          content={props.enterpriseContent}
          editorPath={props.editorPath}
          modifier='desktop'
          pageContentItemId={props.pageContentItemId}
          style={enterpriseGridStyle}
          wysiwygId={props.wysiwygId}
        />
        {features.map((feature, index) => (
          <React.Fragment key={feature.contentItemId}>
            {renderFeatureLabel(feature)}
            {renderPlanCells(feature, index)}
          </React.Fragment>
        ))}
      </div>

      <div className='PlansTable-cards'>
        {PRICING_PLANS.map((plan) => (
          <div
            className={`PlansTable-card${
              plan.highlighted ? ` PlansTable-card--highlighted` : ``
            }`}
            key={plan.id}
          >
            {renderPlanHeader(plan)}
            <div className='PlansTable-card-features'>
              {features.map((feature, index) => (
                <PlansTableFeatureCell
                feature={feature}
                first={index === 0}
                key={`${plan.id}-${feature.contentItemId}`}
                last={index === features.length - 1}
                plan={plan}
                mobile={true}
                />
              ))}
            </div>
          </div>
        ))}
        <div className='PlansTable-card PlansTable-card--enterprise'>
          {renderPlanHeader(ENTERPRISE_PLAN)}
          <PlansTableEnterprisePanel
            content={props.enterpriseContent}
            editorPath={props.editorPath}
            pageContentItemId={props.pageContentItemId}
            wysiwygId={props.wysiwygId}
          />
        </div>
      </div>
    </div>
  )
}

export default PlansTable
